/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from "react";
import { Steps, Image, App, Alert } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { launchHyperVergeWorkflow } from "../../utils/hyperverge";
import {
  HYPERVERGE_STATUS,
  USERTYPE_ID,
  GET_KYC_STEPS,
  TITLE_TXT,
  DESC_TXT,
  GLOBAL_CONSTANTS,
  KYC_MODE,
  KYC_SESSION_KEYS,
} from "../../constants/constants";
import { useNavigate } from "react-router-dom";
import {
  kycHistoryById,
  getUserDetails,
  kycResults,
  fetchAccessToken,
} from "../../redux/slices/AuthSlice";
import Logo from "../../assets/images/auth-logo.png";
import Loader from "../../components/CustomLoader/Loader";
import { generateRandomHash } from "../../helpers/helpers";
import {
  buildRejectionMetadata,
  buildStepStatusArray,
  getDefaultRejectionFallback,
  getHypervergeWorkflowForStep,
  getManualStepStatuses,
  getUiStepStatus,
  hasAnyRejectedManualStep,
  hasSubmittedManualProgress,
  isEntityOrNbfcUser,
  isFullyKycVerified,
  isManualKycSubmittedComplete,
  resolveAutoAdvanceStep,
} from "../../helpers/manualKyc";
import KycSuccessModal from "./KycSuccessModal";
import "./Kyc.scss";
import KycStepContent from "./KycStepContent";
import ManualKycForm from "./ManualKycForm";

const { Step } = Steps;

const Kyc = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { notification } = App.useApp();
  const { user, kycHistory, loading, userDetailsLoading } = useSelector(
    (state) => state.auth,
  );

  const [currentStep, setCurrentStep] = useState(0);
  const [stepStatus, setStepStatus] = useState(["process", "wait", "wait"]);
  const [isUnderReview, setIsUnderReview] = useState(false);
  const [isRejected, setIsRejected] = useState(false);
  const [isResubmitting, setIsResubmitting] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [messageShown, setMessageShown] = useState(false);
  const [isManualMode, setIsManualMode] = useState(false);

  const isIndividual = user?.userTypeId === USERTYPE_ID.INDIVIDUAL;
  const isCompany = user?.userTypeId === USERTYPE_ID.ENTITY;
  const isHUF = user?.userTypeId === USERTYPE_ID.HUF;
  const isNbfc = user?.userTypeId === USERTYPE_ID.NBFC;
  const kycSteps = GET_KYC_STEPS(isHUF, isCompany, isNbfc);
  const stepCount = isCompany || isHUF || isNbfc ? 3 : 2;
  const statuses = getManualStepStatuses(user);

  const updateStep = (step) => {
    setCurrentStep(step);
    setStepStatus(buildStepStatusArray(stepStatus.length || 3, step));
  };

  useEffect(() => {
    if (!user?.orgId) return;

    const fetchKycHistory = async () => {
      const payload = {
        orgId: user.orgId,
        kycTransactionId: user?.EnquiryMgmtView?.kycTransactionId,
        kycType: user?.EnquiryMgmtView?.kycType,
        userTypeId: user.userTypeId,
      };
      const res = await dispatch(kycHistoryById(payload));
      if (res.payload?.statusCode !== 200) {
        notification.error({
          message: "Error",
          description: res.payload,
          placement: "topRight",
          duration: 3,
        });
      }
    };
    fetchKycHistory();
  }, [
    dispatch,
    notification,
    user?.EnquiryMgmtView?.kycTransactionId,
    user?.EnquiryMgmtView?.kycType,
    user?.orgId,
    user?.userTypeId,
  ]);

  const rejectedStepsList = buildRejectionMetadata({
    isHuf: isHUF,
    isCompany,
    isNbfc,
    statuses,
  });
  const firstRejected =
    rejectedStepsList[0] || getDefaultRejectionFallback(statuses);

  const handleResubmitKyc = (targetStepIdx = firstRejected.targetStep) => {
    sessionStorage.removeItem(KYC_SESSION_KEYS.MODAL_PROCEEDED);
    setIsResubmitting(true);
    setIsRejected(false);
    setIsUnderReview(false);
    setIsManualMode(true);
    updateStep(
      typeof targetStepIdx === "number"
        ? targetStepIdx
        : firstRejected.targetStep,
    );
  };

  useEffect(() => {
    const invKycStatus = statuses.orgStatusId;
    const invKycType = statuses.kycType;
    const isManual = invKycType === KYC_MODE.MANUAL || isManualMode;
    const isSubmittedComplete = isManualKycSubmittedComplete(
      isIndividual,
      statuses,
    );

    if (invKycStatus === GLOBAL_CONSTANTS.STAT_KYC_REVIEW_ID) {
      setIsUnderReview(!(isManual && !isSubmittedComplete));
      if (!isResubmitting) setIsRejected(false);
    } else if (
      !isResubmitting &&
      (invKycStatus === GLOBAL_CONSTANTS.STAT_REJECTED_ID ||
        user?.userStatusId === GLOBAL_CONSTANTS.STAT_REJECTED_ID ||
        hasAnyRejectedManualStep(statuses))
    ) {
      setIsUnderReview(false);
      setIsRejected(true);
    }

    if (invKycType === KYC_MODE.MANUAL) {
      setIsManualMode(true);
    }

    if (!kycHistory) return;

    const { status } = kycHistory;

    if (
      ((status === HYPERVERGE_STATUS.NEEDS_REVIEW ||
        status === "auto_approved_needs_review") &&
        (!isManual || isSubmittedComplete)) ||
      (invKycStatus === GLOBAL_CONSTANTS.STAT_KYC_REVIEW_ID &&
        (!isManual || isSubmittedComplete))
    ) {
      setIsUnderReview(true);
    } else if (status === HYPERVERGE_STATUS.MANUALLY_DECLINED) {
      if (!messageShown) {
        notification.info({
          message: TITLE_TXT.HV_DECLINED_TITLE,
          description: DESC_TXT.HV_DECLINED_DESC,
          placement: "topRight",
          duration: 5,
        });
        setMessageShown(true);
      }
    }

    if (kycHistory?.isManualMode || invKycType === KYC_MODE.MANUAL) {
      setIsManualMode(true);
    }

    if (!isUnderReview && !isRejected && !isResubmitting) {
      const target = resolveAutoAdvanceStep({
        isIndividual,
        isHUF,
        statuses,
        user,
        kycHistory,
        currentStep,
      });
      if (currentStep !== target) updateStep(target);
    }
  }, [
    kycHistory,
    currentStep,
    isIndividual,
    isCompany,
    isHUF,
    isNbfc,
    notification,
    user,
    isManualMode,
    isResubmitting,
    isUnderReview,
    isRejected,
  ]);

  useEffect(() => {
    if (!isFullyKycVerified(user)) return;

    setIsModalVisible(true);
    setIsUnderReview(false);

    const autoNavigateTimer = setTimeout(() => {
      handleProceed();
    }, 2500);

    return () => clearTimeout(autoNavigateTimer);
  }, [user]);

  const updateStepStatus = (status) => {
    const newStatus = [...stepStatus];
    newStatus[currentStep] = status;
    if (currentStep + 1 < kycSteps.length) {
      newStatus[currentStep + 1] = "process";
    }
    setStepStatus(newStatus);
  };

  const handleStartKyc = async () => {
    try {
      const accessToken = await dispatch(fetchAccessToken());

      if (accessToken.payload.statusCode !== "200") {
        notification.error({
          message: "Error",
          description: accessToken.payload,
          placement: "topRight",
          duration: 3,
        });
        return;
      }

      const { workflowId, transactionId, type } = getHypervergeWorkflowForStep({
        currentStep,
        isIndividual,
        isCompany,
        isHUF,
        isNbfc,
        orgId: user?.orgId,
        random: generateRandomHash(),
      });

      await launchHyperVergeWorkflow(
        accessToken?.payload?.token,
        workflowId,
        transactionId,
        user?.EnquiryMgmtView?.busName ||
          user?.Business?.busName ||
          user?.business?.busName ||
          "",
        async (result) => {
          if (result.status === HYPERVERGE_STATUS.NEEDS_REVIEW) {
            const res = await dispatch(
              kycResults({
                orgId: user.orgId,
                userId: user.userId,
                userTypeId: user.userTypeId,
                transactionId,
                type,
              }),
            );
            if (res.payload.statusCode === 201) {
              setIsUnderReview(
                res.payload.response.status === HYPERVERGE_STATUS.NEEDS_REVIEW,
              );
              dispatch(getUserDetails());
            } else {
              notification.error({
                message: "Error",
                description: res.payload,
                placement: "topRight",
                duration: 5,
              });
            }
            return;
          }

          if (result.status === "error") {
            setIsManualMode(true);
            notification.warning({
              message: TITLE_TXT.HV_UNAVAILABLE_TITLE,
              description: DESC_TXT.HV_UNAVAILABLE_DESC,
              placement: "topRight",
              duration: 5,
            });
            return;
          }

          if (result.status === HYPERVERGE_STATUS.AUTO_APPROVED) {
            const res = await dispatch(
              kycResults({
                orgId: user.orgId,
                userId: user.userId,
                userTypeId: user.userTypeId,
                transactionId,
                type,
              }),
            );
            if (res.payload.statusCode === 201) {
              updateStepStatus("finish");
              setCurrentStep((prev) => prev + 1);
              dispatch(getUserDetails());
            } else {
              notification.error({
                message: "Error",
                description: res.payload,
                placement: "topRight",
                duration: 5,
              });
            }
          }
        },
      );
    } catch (error) {
      console.error("KYC Error:", error);
      setIsManualMode(true);
      notification.warning({
        message: TITLE_TXT.HV_UNAVAILABLE_TITLE,
        description: DESC_TXT.HV_UNAVAILABLE_DESC,
        placement: "topRight",
        duration: 5,
      });
    }
  };

  const handleSwitchToManual = () => setIsManualMode(true);

  const handleManualSubmitSuccess = () => {
    setIsResubmitting(false);
    setIsUnderReview(true);
    setIsManualMode(false);
    dispatch(getUserDetails());
  };

  const handleCancelManual = () => {
    setIsResubmitting(false);
    setIsManualMode(false);
  };

  const handleProceed = async () => {
    setIsModalVisible(false);
    sessionStorage.setItem(KYC_SESSION_KEYS.MODAL_PROCEEDED, "true");
    const res = await dispatch(getUserDetails());
    const payloadData = res.payload?.response || res.payload || user;
    const userType = payloadData?.userTypeId || user?.userTypeId;

    navigate(isEntityOrNbfcUser(userType) ? "/sla" : "/nominee", {
      replace: true,
    });
  };

  const handleMaybeLater = () => navigate("/onboarding");

  return (
    <div className="kyc-container">
      {loading ? <Loader /> : null}

      <div className="fixed-logo-container">
        <div className="logo">
          <Image preview={false} src={Logo} alt="Logo" />
        </div>

        {isManualMode && !isUnderReview && (
          <Alert
            message={TITLE_TXT.MANUAL_KYC_TITLE}
            description={DESC_TXT.MANUAL_KYC_DESC}
            type="info"
            showIcon
            className="manual-mode-alert"
          />
        )}
      </div>

      <div className="kyc-card">
        <Steps current={currentStep} size="small" className="kyc-stepper">
          {kycSteps.slice(0, stepCount).map((step, index) => (
            <Step
              key={step.title}
              title={step.title}
              status={getUiStepStatus({
                index,
                currentStep,
                stepStatus,
                statuses,
              })}
            />
          ))}
        </Steps>

        <KycStepContent
          isUnderReview={isUnderReview}
          isRejected={isRejected}
          rejectedSteps={rejectedStepsList}
          rejectReason={firstRejected.reason}
          rejectedStepTitle={firstRejected.title}
          resubmitBtnText={firstRejected.btnText}
          onResubmitKyc={handleResubmitKyc}
          kycSteps={kycSteps}
          currentStep={currentStep}
          handleStartKyc={handleStartKyc}
          handleMaybeLater={handleMaybeLater}
          isManualMode={isManualMode}
          onSwitchToManual={handleSwitchToManual}
        />

        {isManualMode && !isUnderReview && !isRejected && (
          <ManualKycForm
            currentStep={currentStep}
            userTypeId={user?.userTypeId || user?.businessProfile?.userTypeId}
            onSubmitSuccess={handleManualSubmitSuccess}
            onNextStep={(nextIdx) => {
              setIsResubmitting(false);
              updateStep(nextIdx);
            }}
            onCancel={handleCancelManual}
            onMaybeLater={handleMaybeLater}
            hideCancel={hasSubmittedManualProgress({
              kycType: statuses.kycType,
              statuses,
              currentStep,
            })}
          />
        )}
      </div>

      {isModalVisible && (
        <KycSuccessModal
          isModalVisible={isModalVisible}
          setIsModalVisible={setIsModalVisible}
          userDetailsLoading={userDetailsLoading}
          handleProceed={handleProceed}
        />
      )}
    </div>
  );
};

export default Kyc;
